import {
  PartnerDealBriefingRevisionChangeResponse,
  PartnerDealBriefingSectionResponse,
  PartnerDealBriefingVersionResponse,
} from '@interfaces/partners/responses/partner-deal-briefing.response';

/**
 * "O que mudou desde a versão anterior" (card 3878, revisão cirúrgica). Só monta texto: `reason`, `before` e `after`
 * vêm do agente, sem escape, e a tela os mostra SÓ por interpolação (nunca HTML). Nada aqui produz marcação.
 */

/** Quantas mudanças aparecem antes do "ver todas". */
export const REVISION_VISIBLE_CHANGES = 5;

/** Nome do item de uma lista, por seção (ou campo de lista), para "Destaques, card 4". */
const ITEM_NOUNS: Record<string, string> = {
  highlights: 'card',
  cards: 'card',
  pillars: 'pilar',
  items: 'item',
  red_flags: 'red flag',
  rows: 'linha',
  conditions: 'condição',
  steps: 'passo',
  keyDocuments: 'documento',
  sources: 'fonte',
  periodKeys: 'período',
  figureIds: 'coluna',
};

/** Campos com nome de operação. Campo que não está aqui (`figureId`) não entra no rótulo. */
const FIELD_LABELS: Record<string, string> = {
  title: 'título',
  subtitle: 'subtítulo',
  borrower: 'tomadora',
  deal: 'negócio',
  origin: 'origem',
  versionDate: 'data da versão',
  decision: 'decisão',
  conditions: 'condições',
  summary: 'síntese',
  text: 'texto',
  label: 'rótulo',
  comment: 'comentário',
  level: 'nível',
  sources: 'fontes',
  ebitdaNote: 'nota do EBITDA',
  steps: 'passos',
  keyDocuments: 'documentos-chave',
  owner: 'responsável',
  periodKeys: 'períodos',
};

/** Seções que são listas de itens no conteúdo: o índice logo depois da seção é o item. */
const SECTION_LIST_FIELD: Record<string, string> = {
  highlights: 'cards',
  pillars: 'items',
  red_flags: 'items',
  indicators: 'rows',
};

const SEGMENT = /([A-Za-z_][A-Za-z0-9_]*)|\[(\d+)\]/g;

/** Nome da seção sem o complemento entre parênteses ("Destaques (5 cards)" vira "Destaques"). */
function sectionName(key: string, catalog: readonly PartnerDealBriefingSectionResponse[]): string {
  const title = catalog.find(s => s.key === key)?.title ?? key;
  return title.replace(/\s*\([^)]*\)\s*$/, '').trim() || key;
}

/**
 * Rótulos de negócio para o "O que mudou" (card 3878): nome de cada card de destaque e título de cada pilar da versão,
 * e o rótulo de cada figura (número da base) pelo id. Montado do contexto do briefing (`content` e `base` do
 * `chat-context`); sem ele, as linhas de troca de figura somem e cards e pilares ficam com o número.
 */
export interface RevisionLabelContext {
  /** Rótulo do card de destaque, por índice. */
  cardLabels: readonly (string | null)[];
  /** Título do pilar, por índice. */
  pillarTitles: readonly (string | null)[];
  /** Rótulo de negócio da figura ("Folga de captação 2025"), pelo id da figura. */
  figureLabels: Readonly<Record<string, string>>;
  /** Decisão da recomendação da versão (PROCEED, HOLD, DECLINE), para a sugestão do chat. */
  decision?: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Lê os rótulos do contexto do briefing. `content` é o conteúdo da versão (`highlights.cards[].label`,
 * `pillars.items[].title`); `base` traz `figures[]` com `id`, `label` e `periodKey`. Formato inesperado vira vazio.
 */
export function buildRevisionLabelContext(content: unknown, base: unknown): RevisionLabelContext {
  const c = asRecord(content);
  const cards = asArray(asRecord(c?.['highlights'])?.['cards']);
  const pillars = asArray(asRecord(c?.['pillars'])?.['items']);
  const figureLabels: Record<string, string> = {};
  for (const figure of asArray(asRecord(base)?.['figures'])) {
    const f = asRecord(figure);
    const id = text(f?.['id']);
    const label = text(f?.['label']);
    if (!id || !label) continue;
    const period = text(f?.['periodKey']);
    figureLabels[id] = period && !label.includes(period) ? `${label} ${period}` : label;
  }
  return {
    cardLabels: cards.map(card => text(asRecord(card)?.['label'])),
    pillarTitles: pillars.map(pillar => text(asRecord(pillar)?.['title'])),
    figureLabels,
    decision: text(asRecord(c?.['recommendation'])?.['decision']),
  };
}

/** Campos que guardam id de figura: o valor é trocado pelo rótulo, e sem rótulo a linha some. */
const FIGURE_FIELDS = new Set(['figureId', 'figureIds']);

/**
 * Nome do campo depois do item ("Destaque Folga de captação 2025: comentário"). Campo principal do item (o texto do
 * pilar) não leva nome: "Pilar Liquidez" já diz.
 */
const ITEM_FIELD_LABELS: Record<string, string | null> = {
  comment: 'comentário',
  label: 'título',
  title: 'título',
  text: null,
  figureId: 'valor',
  pendingReason: 'motivo',
};

/** Campos da recomendação com o nome que a pessoa vê no documento. */
const RECOMMENDATION_FIELDS: Record<string, string> = {
  summary: 'Síntese',
  decision: 'Decisão',
  conditions: 'Condições',
};

function pathSegments(path: string): (string | number)[] {
  const segments: (string | number)[] = [];
  for (const match of (path ?? '').matchAll(SEGMENT)) {
    segments.push(match[2] !== undefined ? Number(match[2]) : match[1]);
  }
  return segments;
}

/** "Destaque Folga de captação 2025: comentário", "Pilar Liquidez", ou "Destaque 4: valor" sem rótulo. */
function describeItem(
  noun: string,
  index: number,
  name: string | null | undefined,
  field: string | undefined
): string {
  const item = name ? `${noun} ${name}` : `${noun} ${index + 1}`;
  const fieldLabel = field ? ITEM_FIELD_LABELS[field] : undefined;
  return fieldLabel ? `${item}: ${fieldLabel}` : item;
}

/**
 * Lugar da mudança em linguagem de negócio: `recommendation.summary` vira "Síntese"; `recommendation.conditions[1]`,
 * "Condição 2"; `highlights.cards[3].comment`, "Destaque Folga de captação: comentário" (ou "Destaque 4:
 * comentário" sem o rótulo); `pillars.items[2].text`, "Pilar Liquidez". Nunca mostra chave de campo: o resto cai no
 * nome da seção com o item e o campo conhecido.
 */
export function describeRevisionPath(
  path: string,
  catalog: readonly PartnerDealBriefingSectionResponse[],
  labels: RevisionLabelContext | null = null
): string {
  const segments = pathSegments(path);
  const [section, ...rest] = segments;
  if (typeof section !== 'string') return path ?? '';

  if (section === 'recommendation') {
    const [field, index] = rest;
    if (field === 'conditions' && typeof index === 'number') return `Condição ${index + 1}`;
    if (typeof field === 'string' && RECOMMENDATION_FIELDS[field])
      return RECOMMENDATION_FIELDS[field];
  }

  if (section === 'highlights' || section === 'pillars') {
    // `highlights.cards[3].x` e o atalho sem a lista `highlights[3].x`.
    const list = rest[0] === SECTION_LIST_FIELD[section] ? rest.slice(1) : rest;
    const [index, field] = list;
    if (typeof index === 'number') {
      const isCard = section === 'highlights';
      const name = isCard ? labels?.cardLabels[index] : labels?.pillarTitles[index];
      return describeItem(isCard ? 'Destaque' : 'Pilar', index, name, field as string | undefined);
    }
  }

  const parts = [sectionName(section, catalog)];
  let lastField: string = section;
  rest.forEach((segment, index) => {
    if (typeof segment === 'number') {
      const noun = ITEM_NOUNS[lastField] ?? 'item';
      // `conditions[1]` já diz "condição 2": tira o "condições" que entrou antes.
      if (parts[parts.length - 1] === FIELD_LABELS[lastField]) parts.pop();
      parts.push(`${noun} ${segment + 1}`);
      return;
    }
    // A lista da seção (`highlights.cards`, `pillars.items`) não vira rótulo: o item já diz.
    const isSectionList = index === 0 && SECTION_LIST_FIELD[section] === segment;
    // `pillars.items[0]` é "pilar 1" (nome da seção); `indicators.rows[2]` é "linha 3" (nome da lista).
    lastField = isSectionList && ITEM_NOUNS[section] ? section : segment;
    const label = FIELD_LABELS[segment];
    if (!isSectionList && label) parts.push(label);
  });
  return parts.join(', ');
}

/** Valor de uma mudança pronto para interpolação: texto corrido, ou JSON indentado para lista e objeto. */
export interface RevisionValue {
  text: string;
  /** Lista ou objeto: a tela mostra em `<pre>`. */
  structured: boolean;
}

export function formatRevisionValue(value: unknown): RevisionValue | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.trim() ? { text: value, structured: false } : null;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return { text: String(value), structured: false };
  }
  if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
    return { text: value.join('; '), structured: false };
  }
  return { text: JSON.stringify(value, null, 2), structured: true };
}

export type RevisionChangeKind = 'changed' | 'added' | 'removed' | 'omitted';

export interface RevisionChangeRow {
  label: string;
  kind: RevisionChangeKind;
  before: RevisionValue | null;
  after: RevisionValue | null;
}

export interface RevisionSummary {
  /** Número da versão anterior (`v{n}`); nulo se ela não estiver na lista. */
  previousNumber: number | null;
  /** Documento refeito inteiro: a tela mostra só a frase com o motivo. */
  regenerated: boolean;
  reason: string | null;
  rows: RevisionChangeRow[];
  /** Mudanças que o backend não mandou (formato ou teto), para "e mais N". */
  omittedCount: number;
}

/**
 * Valor de campo de figura com o rótulo de negócio no lugar do id. Nulo quando algum id não tem rótulo: a linha some
 * (nunca mostra id interno).
 */
function figureValue(
  value: unknown,
  labels: RevisionLabelContext | null
): { ok: boolean; value: unknown } {
  if (value === null || value === undefined) return { ok: true, value };
  const ids = Array.isArray(value) ? value : [value];
  const named = ids.map(id => (typeof id === 'string' ? labels?.figureLabels[id] : undefined));
  if (named.some(label => !label)) return { ok: false, value: null };
  return { ok: true, value: Array.isArray(value) ? named : named[0] };
}

/** Último nome de campo do caminho (`highlights.cards[3].figureId` dá `figureId`). */
function lastField(path: string): string | null {
  const names = pathSegments(path).filter((s): s is string => typeof s === 'string');
  return names.length ? names[names.length - 1] : null;
}

function toRow(
  change: PartnerDealBriefingRevisionChangeResponse,
  catalog: readonly PartnerDealBriefingSectionResponse[],
  labels: RevisionLabelContext | null
): RevisionChangeRow | null {
  const label = describeRevisionPath(change.path, catalog, labels);
  if (change.valueOmitted) return { label, kind: 'omitted', before: null, after: null };

  let rawBefore = change.before;
  let rawAfter = change.after;
  const field = lastField(change.path);
  if (field && FIGURE_FIELDS.has(field)) {
    const before = figureValue(rawBefore, labels);
    const after = figureValue(rawAfter, labels);
    if (!before.ok || !after.ok) return null;
    rawBefore = before.value;
    rawAfter = after.value;
  }

  const before = formatRevisionValue(rawBefore);
  const after = formatRevisionValue(rawAfter);
  const kind: RevisionChangeKind = !before ? 'added' : !after ? 'removed' : 'changed';
  return { label, kind, before, after };
}

/**
 * Resumo da revisão de uma versão de ajuste; nulo quando não há o que mostrar (geração nova, revisão vazia). `labels`
 * dá os nomes de negócio (cards, pilares, figuras); sem ele, troca de figura não aparece.
 */
export function buildRevisionSummary(
  version: PartnerDealBriefingVersionResponse | null,
  versions: readonly PartnerDealBriefingVersionResponse[],
  catalog: readonly PartnerDealBriefingSectionResponse[],
  labels: RevisionLabelContext | null = null
): RevisionSummary | null {
  const revision = version?.revision;
  if (!version || !revision) return null;

  const previousNumber =
    versions.find(v => v.id === version.previousVersionId)?.versionNumber ??
    (version.versionNumber > 1 ? version.versionNumber - 1 : null);
  const regenerated = revision.mode !== 'edit';
  const rows = regenerated
    ? []
    : (revision.changes ?? [])
        .map(change => toRow(change, catalog, labels))
        .filter((row): row is RevisionChangeRow => row !== null);
  const omittedCount = Math.max(0, revision.omittedChanges ?? 0);
  const reason = revision.reason?.trim() || null;

  if (!regenerated && rows.length === 0 && omittedCount === 0) return null;
  return { previousNumber, regenerated, reason, rows, omittedCount };
}

/** Valor curto o bastante para caber ao lado do nome do trecho ("R$ 8,1 MM → R$ 9,5 MM"). */
const SHORT_VALUE_MAX_LENGTH = 24;

/** Antes e depois curtos de uma troca simples (número, múltiplo, rótulo); nulo nas demais. */
export function shortValueChange(row: RevisionChangeRow): { before: string; after: string } | null {
  if (row.kind !== 'changed' || !row.before || !row.after) return null;
  if (row.before.structured || row.after.structured) return null;
  if (
    row.before.text.length > SHORT_VALUE_MAX_LENGTH ||
    row.after.text.length > SHORT_VALUE_MAX_LENGTH
  ) {
    return null;
  }
  return { before: row.before.text, after: row.after.text };
}

/**
 * Linha de resumo do "O que mudou" recolhido (Q11, opção B): a primeira troca simples ("Card Folga de captação
 * (número): R$ 8,1 MM para R$ 9,5 MM") e a contagem de trechos ("4 trechos atualizados").
 */
export function revisionHeadline(summary: RevisionSummary): {
  highlight: string | null;
  count: string;
} {
  let highlight: string | null = null;
  for (const row of summary.rows) {
    const change = shortValueChange(row);
    if (change) {
      highlight = `${row.label} de ${change.before} para ${change.after}`;
      break;
    }
  }
  const total = summary.rows.length + summary.omittedCount;
  return {
    highlight,
    count: total === 1 ? '1 trecho atualizado' : `${total} trechos atualizados`,
  };
}

/** Pedaço de frase do antes/depois; `changed` marca o que mudou (destaque discreto, nunca o parágrafo riscado). */
export interface RevisionTextSegment {
  text: string;
  changed: boolean;
  /** Texto igual recortado ("..."). */
  elided?: boolean;
}

export interface RevisionSentenceDiff {
  before: RevisionTextSegment[];
  after: RevisionTextSegment[];
  /** Havia frases iguais antes do trecho (a tela mostra "..." no começo). */
  trimmedStart: boolean;
  /** Havia frases iguais depois do trecho ("..." no fim). */
  trimmedEnd: boolean;
}

/** Frases do texto, com a pontuação final; número com vírgula ("2,5x") não quebra frase. */
function splitSentences(text: string): string[] {
  return (text.match(/[^.!?;]+(?:[.!?;]+|$)/g) ?? [])
    .map(sentence => sentence.trim())
    .filter(Boolean);
}

/** Palavras do texto (sem os espaços). */
function splitWords(text: string): string[] {
  return text.split(/\s+/).filter(word => word.length > 0);
}

/** Palavras de contexto mantidas em volta de cada mudança; o resto do texto igual vira "...". */
export const REVISION_DIFF_CONTEXT_WORDS = 6;
/** Acima disto (palavras de cada lado), o diff por palavra fica caro; cai no prefixo/sufixo comum. */
const MAX_DIFF_WORDS = 600;

interface MarkedWord {
  word: string;
  changed: boolean;
}

/**
 * Diff por palavra (maior subsequência comum): cada lado com as palavras que não existem no outro marcadas. Texto
 * igual no meio de duas mudanças fica sem marca.
 */
function diffWords(
  before: string[],
  after: string[]
): { before: MarkedWord[]; after: MarkedWord[] } {
  if (before.length > MAX_DIFF_WORDS || after.length > MAX_DIFF_WORDS) {
    return affixDiff(before, after);
  }
  const rows = before.length + 1;
  const cols = after.length + 1;
  const lcs: number[] = new Array(rows * cols).fill(0);
  for (let i = before.length - 1; i >= 0; i--) {
    for (let j = after.length - 1; j >= 0; j--) {
      lcs[i * cols + j] =
        before[i] === after[j]
          ? lcs[(i + 1) * cols + j + 1] + 1
          : Math.max(lcs[(i + 1) * cols + j], lcs[i * cols + j + 1]);
    }
  }
  const markedBefore: MarkedWord[] = [];
  const markedAfter: MarkedWord[] = [];
  let i = 0;
  let j = 0;
  while (i < before.length && j < after.length) {
    if (before[i] === after[j]) {
      markedBefore.push({ word: before[i++], changed: false });
      markedAfter.push({ word: after[j++], changed: false });
    } else if (lcs[(i + 1) * cols + j] >= lcs[i * cols + j + 1]) {
      markedBefore.push({ word: before[i++], changed: true });
    } else {
      markedAfter.push({ word: after[j++], changed: true });
    }
  }
  while (i < before.length) markedBefore.push({ word: before[i++], changed: true });
  while (j < after.length) markedAfter.push({ word: after[j++], changed: true });
  return { before: markedBefore, after: markedAfter };
}

/** Texto longo demais para o diff por palavra: só o prefixo e o sufixo comuns ficam sem marca. */
function affixDiff(
  before: string[],
  after: string[]
): { before: MarkedWord[]; after: MarkedWord[] } {
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start++;
  let end = 0;
  while (
    end < before.length - start &&
    end < after.length - start &&
    before[before.length - 1 - end] === after[after.length - 1 - end]
  ) {
    end++;
  }
  const mark = (words: string[]): MarkedWord[] =>
    words.map((word, index) => ({ word, changed: index >= start && index < words.length - end }));
  return { before: mark(before), after: mark(after) };
}

/**
 * Junta as palavras em pedaços (mudou / não mudou) e recorta o texto igual longe das mudanças em "...", deixando
 * {@link REVISION_DIFF_CONTEXT_WORDS} palavras de contexto de cada lado. O espaço entre pedaços fica no pedaço sem
 * marca, para o destaque não pegar espaço.
 */
function toSegments(words: MarkedWord[]): RevisionTextSegment[] {
  const changedIndexes = words.flatMap((word, index) => (word.changed ? [index] : []));
  const keep = words.map(
    (_, index) =>
      changedIndexes.length === 0 ||
      changedIndexes.some(changed => Math.abs(changed - index) <= REVISION_DIFF_CONTEXT_WORDS)
  );

  // Itens na ordem: palavras mantidas, "..." no lugar de cada trecho igual recortado e o espaço entre eles. O espaço
  // só é marcado entre duas palavras marcadas ("8,1 MM" vira um destaque só).
  const items: RevisionTextSegment[] = [];
  let previousChanged: boolean | null = null;
  words.forEach((word, index) => {
    if (!keep[index]) {
      if (items[items.length - 1]?.elided) return;
      if (items.length) items.push({ text: ' ', changed: false });
      items.push({ text: '...', changed: false, elided: true });
      previousChanged = null;
      return;
    }
    if (items.length) {
      items.push({ text: ' ', changed: previousChanged === true && word.changed });
    }
    items.push({ text: word.word, changed: word.changed });
    previousChanged = word.changed;
  });

  const result: RevisionTextSegment[] = [];
  for (const item of items) {
    const last = result[result.length - 1];
    if (last && !last.elided && !item.elided && last.changed === item.changed) {
      last.text += item.text;
    } else {
      result.push({ ...item });
    }
  }
  return result;
}

/**
 * Antes e depois só da(s) frase(s) que mudaram, com só as palavras trocadas marcadas (Q11, B3 da crítica): frases
 * iguais no começo e no fim saem ("..."), o diff é por palavra (texto igual entre duas mudanças fica sem marca) e o
 * texto igual longe das mudanças é recortado em "...". Formatação de exibição, sem cálculo.
 */
export function diffRevisionSentences(before: string, after: string): RevisionSentenceDiff {
  const beforeSentences = splitSentences(before);
  const afterSentences = splitSentences(after);

  let head = 0;
  while (
    head < beforeSentences.length &&
    head < afterSentences.length &&
    beforeSentences[head] === afterSentences[head]
  ) {
    head++;
  }
  let tail = 0;
  while (
    tail < beforeSentences.length - head &&
    tail < afterSentences.length - head &&
    beforeSentences[beforeSentences.length - 1 - tail] ===
      afterSentences[afterSentences.length - 1 - tail]
  ) {
    tail++;
  }

  const beforeWords = splitWords(
    beforeSentences.slice(head, beforeSentences.length - tail).join(' ')
  );
  const afterWords = splitWords(afterSentences.slice(head, afterSentences.length - tail).join(' '));
  const marked = diffWords(beforeWords, afterWords);

  return {
    before: toSegments(marked.before),
    after: toSegments(marked.after),
    trimmedStart: head > 0,
    trimmedEnd: tail > 0,
  };
}

/**
 * Pedaços de um lado com as reticências das pontas resolvidas: frase igual cortada (`trimmedStart`/`trimmedEnd`) e
 * texto recortado na ponta viram um "..." só.
 */
export function revisionSideWithEllipsis(
  parts: readonly RevisionTextSegment[],
  trimmedStart: boolean,
  trimmedEnd: boolean
): RevisionTextSegment[] {
  if (parts.length === 0) return [];
  const result = [...parts];
  const ellipsis = (): RevisionTextSegment => ({ text: '...', changed: false, elided: true });
  if (trimmedStart && !result[0].elided) result.unshift(ellipsis(), { text: ' ', changed: false });
  if (trimmedEnd && !result[result.length - 1].elided) {
    result.push({ text: ' ', changed: false }, ellipsis());
  }
  return result;
}
